/* global Session */

import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider, css } from 'styled-components';
import { useForm, useWatch, Controller } from 'react-hook-form';

import theme from '../../../../theme';

import { DialogBody } from '../../../components/DialogBody';
import { DialogFooter } from '../../../components/DialogFooter';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { TextField } from '../../../components/TextField';
import { SelectField } from '../../../components/SelectField';

const InstructiveText = styled.p.attrs({
    className: 'text-info'
})`
    white-space: pre-line;
    font-size: 14px;
`;

const ErrorMessage = styled.p`
    flex-basis: 100%;
    color: #a94442;
    font-size: 14px;
    line-height: 23px;
    margin-top: -5px;
    /* color: ${({ theme }) => theme.colors.scales.R8}; */
`;

const Form = styled.form`
    position: relative;
    z-index: 2;
`;

const FormFieldRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin: ${({ nomargin }) => (!nomargin ? '1.5rem 0' : '0')};
    ${({ wrap }) =>
        wrap === 'true' &&
        css`
            flex-wrap: wrap;
        `}
`;

const Button = styled.button.attrs({
    type: 'button'
})`
    margin-left: 4px;
`;

const EligibleCriteriaListLabel = styled.span`
    z-index: 1;
    position: relative;
    color: ${(props) => props.theme.colors.text.muted};
    margin-top: 1rem;
    margin-bottom: 0.25rem;
    font-size: 0.8rem;
`;

const EligibleCriteriaList = styled.ul`
    /* padding: 0;
    margin: 0;
    list-style-type: none; */
    border-radius: 4px;
    z-index: 1;
    position: relative;
    overflow: hidden;
`;

const EligibleCriteria = styled.li`
    padding: 4px 0;
    color: ${(props) => props.theme.colors.text.dark};
`;

const EligibleCriteriaSpan = styled.span`
    color: ${(props) => (props.inactive === 'true' ? props.theme.colors.monochrome.MC2 : props.theme.colors.text.dark)};
`;

const CHAR_CODES = {
    PERIOD: 46,
    e: 101,
    E: 69,
    MINUS: 45,
    PLUS: 43
};

const durationTransforms = {
    DAYS: (value) => Number(value),
    WEEKS: (value) => Number(value) * 7,
    MONTHS: (value) => Number(value) * 30,
    YEARS: (value) => Number(value) * 365
};
const durationToDays = (duration, unit) => {
    return durationTransforms[unit]?.(duration);
};
const unitOptions = [
    { label: 'Day(s)', value: 'DAYS' },
    { label: 'Week(s)', value: 'WEEKS' },
    { label: 'Month(s)', value: 'MONTHS' },
    { label: 'Year(s)', value: 'YEARS' }
];
const getDefaultFormState = (config = {}) => ({
    enabled: config.enabled,
    duration: typeof config?.duration !== 'undefined' && config.duration !== null ? config.duration.toString() : '',
    unit: config?.unit ? unitOptions.find((option) => option.value === config.unit) : ''
});
const DurationFieldStyle = {
    width: '7rem',
    height: '47px'
};
export const TimeboxWorkflow = ({
    getEligibleCriteriaList,
    getTimeboxConfig,
    applyChanges,
    containerQuerySelector,
    ...props
}) => {
    const [isLoading, setIsLoading] = React.useState('');
    const [eligibleCriteriaList, setEligibleCriteriaList] = React.useState([]);
    const systemProperties = React.useMemo(
        () => ({
            guidanceText: Session.getSystemProperty('TIMEBOX_GUIDANCE_TEXT', 'rckms'),
            minDays: Number(Session.getSystemProperty('TIMEBOX_MIN_DURATION_DAYS', 'rckms')),
            maxDays: Number(Session.getSystemProperty('TIMEBOX_MAX_DURATION_DAYS', 'rckms'))
        }),
        []
    );
    const {
        register,
        control,
        handleSubmit,
        trigger,
        reset,
        formState: { isDirty, errors }
    } = useForm({
        reValidateMode: 'onChange',
        defaultValues: getDefaultFormState()
    });
    const unit = useWatch({
        control,
        name: 'unit'
    });
    const duration = useWatch({
        control,
        name: 'duration'
    });

    const onSubmit = (values) => {
        applyChanges({
            enabled: Boolean(values.enabled),
            duration: typeof values?.duration !== 'undefined' ? Number(values.duration) : undefined,
            unit: values?.unit?.value
        });
    };

    const Save = (closeOnSuccess) => {
        setIsLoading('loading');
        handleSubmit((data) => {
            onSubmit(data);
            reset(data);
            setIsLoading('success');
            if (closeOnSuccess) $(containerQuerySelector).modal('hide');
        })();
        setIsLoading('');
    };

    const onApply = Save.bind(null, false);
    const onSave = Save.bind(null, true);

    React.useEffect(() => {
        window.$(containerQuerySelector).on('show.bs.modal', (e) => {
            setEligibleCriteriaList(getEligibleCriteriaList());
            reset(getDefaultFormState(getTimeboxConfig()));
        });
        window.$(containerQuerySelector).on('hidden.bs.modal', (e) => {
            setIsLoading('');
        });

        return () => {
            window.$(containerQuerySelector).off('show.bs.modal');
            window.$(containerQuerySelector).off('hidden.bs.modal');
        };
    }, []);

    React.useEffect(() => {
        if (unit) {
            trigger('duration');
        }
    }, [unit]);

    return (
        <ThemeProvider theme={theme}>
            <div
                id="timebox-modal-el"
                className={`modal-dialog modal-lg`}
                aria-modal="true"
                aria-labelledby="timebox-modal-title"
                style={{
                    maxWidth: '700px'
                }}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <h4 className="modal-title">
                            <span id="timebox-modal-title">Edit Duration</span>
                        </h4>
                    </div>
                    <DialogBody style={{ overflow: 'visible', paddingBottom: 0 }}>
                        <InstructiveText>{systemProperties.guidanceText}</InstructiveText>

                        <Form>
                            <FormFieldRow>
                                <span style={{ fontSize: '14px', whiteSpace: 'nowrap', lineHeight: '48px' }}>
                                    Set Timebox Duration For:
                                </span>
                                <FormFieldRow wrap="true" nomargin>
                                    <div style={DurationFieldStyle}>
                                        <TextField
                                            error={errors?.duration}
                                            inputProps={{
                                                suppressErrorMessage: true,
                                                style: DurationFieldStyle
                                            }}
                                            style={DurationFieldStyle}
                                            type="number"
                                            step="1"
                                            placeholder="Duration..."
                                            tabIndex="0"
                                            id="duration"
                                            autocomplete="false"
                                            min="0"
                                            size={`sm`}
                                            onKeyPress={(event) => {
                                                const charCode = event.charCode;

                                                if (
                                                    [
                                                        CHAR_CODES.PERIOD,
                                                        CHAR_CODES.e,
                                                        CHAR_CODES.E,
                                                        CHAR_CODES.MINUS,
                                                        CHAR_CODES.PLUS
                                                    ].includes(charCode)
                                                ) {
                                                    event.preventDefault();
                                                } else {
                                                    return true;
                                                }
                                            }}
                                            {...register('duration', {
                                                required: 'Required',
                                                validate: {
                                                    required: (val) => Boolean(val) || 'Duration Value required.',
                                                    intOnly: (val) =>
                                                        val.toString().match(/^\d*$/) !== null ||
                                                        `Duration value must be an integer.`,
                                                    min: (val) =>
                                                        durationToDays(val, unit?.value) >= systemProperties.minDays ||
                                                        `Minimum duration is ${systemProperties.minDays} days`,
                                                    max: (val) =>
                                                        durationToDays(val, unit?.value) <= systemProperties.maxDays ||
                                                        `Maximum duration is ${systemProperties.maxDays} days`
                                                }
                                            })}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            width: '12rem'
                                        }}
                                    >
                                        <Controller
                                            control={control}
                                            name="unit"
                                            rules={{ required: true }}
                                            render={({ field, fieldState }) => {
                                                const { ref, ...fieldProps } = field;

                                                return (
                                                    <SelectField
                                                        inputProps={{
                                                            suppressErrorMessage: true
                                                        }}
                                                        options={unitOptions}
                                                        placeholder="Select unit..."
                                                        tabIndex="0"
                                                        id="unit"
                                                        size={`sm`}
                                                        error={fieldState?.error}
                                                        menuPosition={'absolute'}
                                                        {...fieldProps}
                                                    />
                                                );
                                            }}
                                        />
                                    </div>
                                    {Object.keys(errors).length ? (
                                        <ErrorMessage>
                                            {!unit?.value || !duration
                                                ? 'Both duration and unit are required fields.'
                                                : null}
                                            {unit?.value && duration && errors?.duration?.message
                                                ? errors.duration.message
                                                : null}
                                        </ErrorMessage>
                                    ) : null}
                                </FormFieldRow>
                            </FormFieldRow>
                        </Form>
                        <EligibleCriteriaListLabel>
                            The following Criteria are Timebox Eligible
                        </EligibleCriteriaListLabel>
                        <EligibleCriteriaList>
                            {eligibleCriteriaList.map((el, index) => (
                                <EligibleCriteria key={index}>
                                    <EligibleCriteriaSpan inactive={Boolean(el?.inactive).toString()}>
                                        {el.label} {el.inactive ? <span>[INACTIVE]</span> : null}
                                    </EligibleCriteriaSpan>
                                </EligibleCriteria>
                            ))}
                        </EligibleCriteriaList>
                    </DialogBody>
                    <DialogFooter>
                        {isLoading === 'loading' && <div style={{ marginRight: '0.5rem' }}>Loading...</div>}
                        {isLoading === 'success' && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            className="btn btn-primary"
                            onClick={onSave}
                            disabled={isLoading === 'loading' || !isDirty}
                        >
                            Save Duration
                        </Button>
                        <Button
                            className="btn btn-default"
                            onClick={onApply}
                            disabled={isLoading === 'loading' || !isDirty}
                        >
                            Apply
                        </Button>
                        <Button className="btn btn-default" data-dismiss="modal">
                            Close
                        </Button>
                    </DialogFooter>
                </div>
            </div>
        </ThemeProvider>
    );
};

TimeboxWorkflow.propTypes = {
    getEligibleCriteriaList: PropTypes.func,
    getTimeboxConfig: PropTypes.func,
    applyChanges: PropTypes.func,
    containerQuerySelector: PropTypes.string
};

export default TimeboxWorkflow;
